import { AxiosResponse } from 'axios';

import { initializeApiInterceptors } from './axiosInterceptors';
import { HttpClient } from './httpClient';

export type ResponseHandler<T = any, R = T> = (response: AxiosResponse<T>) => R;

interface ApiServiceGetConfig {
	params: any;
	headers?: any;
}

function statusChecker<T>(response: AxiosResponse<T>) {
	// Accept 2xx (success) and 3xx (redirect) status codes as successful
	if (response?.status >= 200 && response?.status < 400) {
		return response;
	}

	throw response?.data;
}

class ApiService {
	private _httpClient = HttpClient.getInstance();
	private _customBaseUrl: string | null = null;

	constructor() {
		initializeApiInterceptors(this._httpClient);
	}

	responseHandler: ResponseHandler = ({ data }) => data ?? null;

	// Update the base URL and recreate the HTTP client
	setBaseUrl(baseUrl: string) {
		this._customBaseUrl = baseUrl;
		// Note: HttpClient.getInstance() is used, so we can't recreate it here
		// The base URL change would need to be handled at the HttpClient level if needed
	}

	// Get current base URL
	getBaseUrl(): string {
		return this._customBaseUrl || process.env.REACT_APP_API_URL || '';
	}

	async get<T = any>(url: string, config?: ApiServiceGetConfig): Promise<AxiosResponse<T>> {
		const { params, headers } = config ?? {};

		return statusChecker(await this._httpClient.get<T>(url, { params, headers }));
	}

	async post<T = any, B = any>(url: string, body?: B, headers?: any): Promise<AxiosResponse<T>> {
		return statusChecker(await this._httpClient.post<T>(url, body, { headers }));
	}

	async postUpload<T = any, B = any>(url: string, body?: B): Promise<AxiosResponse<T>> {
		return statusChecker(
			await this._httpClient.post<T>(url, body, {
				headers: { 'Content-Type': 'multipart/form-data' },
			})
		);
	}

	async put<T = any, B = any>(url: string, body: B): Promise<AxiosResponse<T>> {
		return statusChecker(await this._httpClient.put<T>(url, body));
	}

	async delete(url: string): Promise<AxiosResponse<null>> {
		return statusChecker(await this._httpClient.delete(url));
	}

	async patch<T = any, B = any>(url: string, body: B): Promise<AxiosResponse<T>> {
		return statusChecker(
			await this._httpClient.patch<T>(url, body, {
				headers: { 'Content-Type': 'application/merge-patch+json' },
			})
		);
	}
}

export const apiService = new ApiService();
